/**
 * ONE mapping from what the shop query returns to what a shop card renders.
 *
 * It used to be written three times — once on the server page, once for the
 * filter refetch, once for load-more — and they drifted. A car mapped on the
 * first page kept its `motors` details and rendered as a car; the moment a
 * visitor filtered or pressed Load more, the same car came back through a
 * mapping that had never heard of cars and rendered as an ordinary
 * electronics tile. Menu items and property listings had the same hole.
 *
 * So: one function, three callers. A new bucket added here reaches every
 * path of the storefront at once. (The product page's own mapping lives in
 * ProductPageClient — see reference_shop_product_mappers.)
 *
 * PRIVACY: nothing is stripped here. A dealer's floor price and a landlord's
 * owner phone never leave the server — convex/products/shop.ts#publicProduct
 * removes them before the data is sent — because a client-side strip still
 * ships the secret to the browser.
 */

import type { ShopProduct } from "@/lib/types/shop";

export interface ToShopProductOptions {
  /** The brand these products belong to. */
  brandId: string;
  /** The storefront's industry, used when a product carries none of its own. */
  industry: string;
  /**
   * Live stock for menu items, by product id — what `menuStockIdsFor` asked
   * `foodBeverage/storefront:getMenuStockFor` for. Bottled drinks sell out;
   * made-to-order food never does.
   *
   * Present: a tracked menu item missing from the map has no stock (0).
   * Absent: the stock is not known (the read failed) — the row's own figures
   * when it carries any, else the item is NOT called sold out. The checkout
   * still refuses what the shelf does not have; a drink wrongly shown sold
   * out is a sale lost for nothing (KIOTA's "coke", 50 at the till, read
   * SOLD OUT on the website, 29 Oct 2026).
   */
  menuStock?: Map<string, number>;
}

/** A menu item (food or drink) — it renders as a menu card. */
export function isMenuRow(p: any): boolean {
  return !!p && (p.industry === "food-beverage" || !!p.food);
}

/**
 * The menu items on a page whose stock decides whether they can be ordered:
 * tracked ones (bottled drinks, packaged goods). Made-to-order dishes
 * (`trackStock: false`) never sell out, so they are never asked about.
 */
export function menuStockIdsFor(rows: any[] | null | undefined): string[] {
  const out: string[] = [];
  const seen = new Set<string>();
  for (const p of rows || []) {
    if (!isMenuRow(p) || p.trackStock === false || !p._id) continue;
    const id = String(p._id);
    if (seen.has(id)) continue;
    seen.add(id);
    out.push(id);
  }
  return out;
}

/** Stock rows from getMenuStockFor → the map toShopProduct reads. */
export function menuStockMap(rows: { productId: string; qty: number }[] | null | undefined): Map<string, number> {
  const m = new Map<string, number>();
  for (const r of rows || []) m.set(String(r.productId), Math.max(0, Number(r.qty) || 0));
  return m;
}

/**
 * What a menu card shows for stock — the one rule that decides "Sold out"
 * on the website grid.
 */
export function menuCardStock(p: any, menuStock?: Map<string, number>): { stock: number; inStock: boolean } {
  if (p?.trackStock === false) {
    // Made to order: never sold out, whatever the figure (no shelf behind it).
    const own = Number(p.totalStock ?? p.stock ?? 0);
    return { stock: Number.isFinite(own) && own > 0 ? own : 0, inStock: true };
  }
  if (menuStock) {
    const qty = menuStock.get(String(p?._id)) ?? 0;
    return { stock: qty, inStock: qty > 0 };
  }
  // Unknown: trust a figure the row carries (stockedCardRowFor rows do),
  // otherwise do not claim sold out.
  const own = p?.totalStock ?? p?.stock;
  if (typeof own === "number" && Number.isFinite(own)) return { stock: own, inStock: own > 0 };
  return { stock: 1, inStock: true };
}

export function toShopProduct(p: any, opts: ToShopProductOptions): ShopProduct {
  // Food & Beverage menu items need the food bucket, their own industry and
  // REAL stock; everything else keeps the long-standing always-in-stock shape.
  const isFoodItem = isMenuRow(p);
  const menu = isFoodItem ? menuCardStock(p, opts.menuStock) : null;
  const liveStock = menu ? menu.stock : 1;

  return {
    _id: p._id,
    name: p.name,
    slug: p.slug || String(p.name || "").toLowerCase().replace(/\s+/g, "-"),
    price: p.price,
    comparePrice: p.compareAtPrice,
    images: p.images || [],
    description: p.description,
    stock: liveStock,
    totalStock: liveStock,
    ...(isFoodItem
      ? { food: p.food, trackStock: p.trackStock, priceVariesByVariant: p.priceVariesByVariant }
      : {}),
    // The buckets a card is built from. Each one is an allow-list entry: a
    // product whose bucket is missing here renders as a plain tile.
    ...(p.motors ? { motors: p.motors } : {}),
    ...(p.property ? { property: p.property } : {}),
    hire: p.hire || undefined,
    // What it comes with, and the extras it offers (the card's "+ add-ons").
    hireKit: p.hireKit || undefined,
    // The card's "Free delivery" badge (website orders, Oct 2026).
    freeDeliveryOnline: p.freeDeliveryOnline === true ? true : undefined,
    // Sizes on the card only when the shop shows them (Events & Decor).
    showSizesOnWebsite: typeof p.showSizesOnWebsite === "boolean" ? p.showSizesOnWebsite : undefined,
    brandId: opts.brandId as any,
    brandName: p.brandName,
    brandSlug: p.brandSlug,
    category: p.categoryName || "",
    subcategoryName: p.subcategoryName,
    subcategorySlug: p.subcategorySlug,
    productTypeName: p.productTypeName,
    productTypeSlug: p.productTypeSlug,
    storeName: p.storeName,
    storeCode: p.storeCode,
    inStock: menu ? menu.inStock : true,
    industry: (p.industry || opts.industry) as any,
    condition: p.condition || "New",
    colorVariants: p.colorVariants || [],
    fashionAttributes: p.fashionAttributes || undefined,
    // Electronics variant axes + variants, so the card can open the
    // colour/storage selector and resolve per-variant prices.
    electronicsAttributes: p.electronicsAttributes || undefined,
    variants: p.variants || undefined,
    hasVariants: p.hasVariants ?? false,
    currency: p.currency,
    isCustomPrice: p.isCustomPrice || false,
    priceHidden: p.priceHidden || false,
  };
}
